import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useAppSnackbar } from '@/stores/app/snackbar';
import {
  searchJobProfilesForWizard,
  getJobProfile,
  getJobProfileHris,
  getFinalizedProfileTemplates,
  createJobProfile,
} from '@/api/jobProfile';

export const useCreateJobProfileWizard = defineStore('createJobProfileWizard', () => {
  const { addErrorSnackbar, addMultilineErrorSnackbar } = useAppSnackbar();

  // ─── step ───────────────────────────────────────────────────────────────────
  const step = ref(1);

  // ─── step 1: form data ───────────────────────────────────────────────────────
  const formTitle = ref('');
  const formJobCode = ref('');
  const formLocation = ref('');

  // ─── step 1: search ──────────────────────────────────────────────────────────
  const searchResults = ref([]);
  const searchLoading = ref(false);

  // ─── step 1 & 2: profile preview ─────────────────────────────────────────────
  const previewProfile = ref(null);
  const previewLoading = ref(false);
  const selectedProfileUlid = ref(null);

  // ─── step 2: templates ────────────────────────────────────────────────────────
  const templates = ref([]);
  const templatesLoading = ref(false);
  const selectedTemplateUlid = ref(null);
  const templatePreview = ref(null);
  const templatePreviewLoading = ref(false);

  const mergedProfileAndTemplatePreview = computed(() => {
    if (!templatePreview.value && !previewProfile.value) return null;
    if (!templatePreview.value) return previewProfile.value;
    if (!previewProfile.value) return templatePreview.value;

    const profileFieldsByType = Object.fromEntries(
      (previewProfile.value.fields || []).map((f) => [f.type ?? f.id, f]),
    );

    const mergedFields = (templatePreview.value.fields || []).map((templateField) => {
      const key = templateField.type ?? templateField.id;
      const profileField = profileFieldsByType[key];
      return {
        ...templateField,
        blocks: profileField?.blocks ?? templateField.blocks,
      };
    });

    return {
      ...templatePreview.value,
      fields: mergedFields,
    };
  });

  // ─── creating ────────────────────────────────────────────────────────────────
  const createLoading = ref(false);

  // ─── actions ─────────────────────────────────────────────────────────────────

  const searchProfiles = async () => {
    if (!formTitle.value && !formJobCode.value) {
      searchResults.value = [];
      return;
    }
    searchLoading.value = true;
    try {
      const { documents } = await searchJobProfilesForWizard({
        title: formTitle.value,
        jobCode: formJobCode.value,
      });
      searchResults.value = documents || [];
    } catch (err) {
      addErrorSnackbar({ text: `Profile search error: ${err.message || err}` });
      throw err;
    } finally {
      searchLoading.value = false;
    }
  };

  const selectProfileForPreview = async (profile) => {
    if (!profile) {
      selectedProfileUlid.value = null;
      previewProfile.value = null;
      previewLoading.value = false;
      return;
    }

    if (selectedProfileUlid.value === profile.ulid) return;
    selectedProfileUlid.value = profile.ulid;
    previewProfile.value = null;
    previewLoading.value = true;
    try {
      const loader = profile.is_hris_profile ? getJobProfileHris : getJobProfile;
      previewProfile.value = await loader({ ulid: profile.ulid, rescore: false });
    } catch (err) {
      addErrorSnackbar({ text: `Profile preview error: ${err.message || err}` });
      selectedProfileUlid.value = null;
      throw err;
    } finally {
      previewLoading.value = false;
    }
  };

  const goToStep2 = () => {
    step.value = 2;
  };

  const goToStep1 = () => {
    step.value = 1;
  };

  const loadTemplates = async () => {
    templatesLoading.value = true;
    try {
      const { templates: list } = await getFinalizedProfileTemplates();
      templates.value = list || [];
    } catch (err) {
      addErrorSnackbar({ text: `Templates load error: ${err.message || err}` });
      throw err;
    } finally {
      templatesLoading.value = false;
    }
  };

  const selectTemplate = async (ulid) => {
    if (selectedTemplateUlid.value === ulid) return;
    selectedTemplateUlid.value = ulid;
    templatePreview.value = null;
    templatePreviewLoading.value = true;
    try {
      templatePreview.value = await getJobProfile({ ulid, rescore: false });
    } catch (err) {
      addErrorSnackbar({ text: `Template preview error: ${err.message || err}` });
      selectedTemplateUlid.value = null;
      throw err;
    } finally {
      templatePreviewLoading.value = false;
    }
  };

  const createProfile = async (router) => {
    createLoading.value = true;
    try {
      const response = await createJobProfile({
        title: formTitle.value?.trim(),
        jobCode: formJobCode.value?.trim(),
        location: formLocation.value?.trim(),
        templateUlid: selectedTemplateUlid.value,
        profileUlid: previewProfile.value?.ulid,
      });
      await router.push({ name: 'job-profile-view', params: { id: response.document_ulid } });
    } catch (err) {
      addMultilineErrorSnackbar({
        text: `An error occurred while creating this profile.
        Our support team will contact you once the issue has been resolved.
        ${err.message}`,
      });
      throw err;
    } finally {
      createLoading.value = false;
    }
  };

  const reset = () => {
    step.value = 1;
    formTitle.value = '';
    formJobCode.value = '';
    formLocation.value = '';
    searchResults.value = [];
    searchLoading.value = false;
    previewProfile.value = null;
    previewLoading.value = false;
    selectedProfileUlid.value = null;
    templates.value = [];
    templatesLoading.value = false;
    selectedTemplateUlid.value = null;
    templatePreview.value = null;
    templatePreviewLoading.value = false;
    createLoading.value = false;
  };

  return {
    // state
    step,
    formTitle,
    formJobCode,
    formLocation,
    searchResults,
    searchLoading,
    previewProfile,
    previewLoading,
    selectedProfileUlid,
    templates,
    templatesLoading,
    selectedTemplateUlid,
    templatePreview,
    templatePreviewLoading,
    mergedProfileAndTemplatePreview,
    createLoading,

    // actions
    searchProfiles,
    selectProfileForPreview,
    goToStep1,
    goToStep2,
    loadTemplates,
    selectTemplate,
    createProfile,
    reset,
  };
});
